import { BASE_PATH } from '@trinnvis/dabih-api-client';

import type { State } from 'src/store/types.js';
import { variantAssetsView, variantSimplifiedAssets } from 'src/store/selectors/variants.js';
import { key } from 'src/store/selectors/selectors';
import type { EventDataForAsset } from 'src/store/selectors';
import { assetsWithTasks } from 'src/store/selectors';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import { toAssetViewModel } from '../../store/selectors/assets/to-asset-view-model';
import { employeesNotDeleted } from '../../store/selectors/employees/employees-not-deleted';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import { tasksForAssets } from '../../store/selectors/responsibilities/tasks-for-assets';
import type { AssetView } from '../../content/assets/asset-view';
import type { AssetViewEditItem } from '../../content/assets/asset-view-edit-item';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchDraft } from '../fetch-draft';
import type { ApiClient } from '../../store/backend';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
function normalizeAssetCategory(category: string, availableCategories: string[]): string {
  if (category !== '' && !availableCategories.includes(category)) {
    return 'Annet';
  }
  return category;
}

export function buildAssetView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): AssetView {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const viewOptions = variantAssetsView(state);

  const item = organization.assetsById[uuid];
  assertEntityFound(item, 'assets', uuid);

  const entity = toAssetViewModel(item, organization);
  const href = currentParent + '/assets/' + uuid;

  const withEvents = assetsWithTasks(state).find((a) => a.uuid === uuid);

  // For deleted assets, withEvents will be undefined since assetsWithTasks only includes non-deleted assets
  // In this case, we provide an empty events array
  const assetEvents: EventDataForAsset[] = withEvents?.events ?? [];

  return {
    ...commonData,
    promoData: getPromoDataForEntity('assets', item, getFeatureStates(state)),
    docsForLinking: [],
    currentUserHasAccess: true,
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    deleted: entity.deleted === true,
    helpContent: '',
    pdfHref: BASE_PATH + '/organizations/' + organization.organizationId + '/assets/' + uuid + '.pdf?key=' + key(state),
    parentHref: currentParent,
    href: href,
    type: 'assets',
    name: entity.name ?? '',
    attachments: toAttachmentItems(api, 'assets', entity.uuid, entity.attachments),
    dsaApproved: entity.dsaApproved,
    dsaRegistered: entity.dsaRegistered,
    documented: entity.documented === true,
    usesArtificialIntelligence: entity.usesArtificialIntelligence === true,
    documentationVerified: entity.documentationVerified === true,
    assetVerified: entity.assetVerified === true,
    maintenanceRequired: entity.maintenanceRequired === true,
    requiresElectricity: entity.requiresElectricity === true,
    radiation: entity.radiation,
    radiationType: entity.radiationType ?? '',
    radiationCategory1: entity.radiationType === 'category1',
    radiationCategory2: entity.radiationType === 'category2',
    radiationCategory3: entity.radiationType === 'category3',
    requiredEventInstanceMissing: entity.requiredEventInstanceMissing,
    hasPersonalDataItem: entity.hasPersonalDataItem,
    maintenance: entity.maintenance ?? '',
    competency: entity.competency ?? '',
    complianceDeclaration: entity.complianceDeclaration ?? '',
    accessory: entity.accessory ?? '',
    discardedYear: entity.discardedYear ?? '',
    acquiredYear: entity.acquiredYear ?? '',
    category: normalizeAssetCategory(entity.category ?? '', viewOptions.categories),
    location: entity.location ?? '',
    manufacturer: entity.manufacturer ?? '',
    model: entity.model ?? '',
    supplier: entity.supplier ?? '',
    number: entity.number ?? '',
    ownerDisplay: entity.ownerDisplay ?? '',
    owner: entity.owner ?? '',
    userManual: entity.userManual ?? '',
    assetEvents,
    relatedTasks: [],
    availableRelatedTasks: tasksForAssets(state)
      .filter((t) => !t.assets.includes(uuid))
      .map((t) => ({ uuid: t.uuid, label: t.name })),
    availableAssetCategories: viewOptions.categories.map((c) => ({ value: c, text: c })),
    availableAssetEmployees: employeesNotDeleted(state)
      .filter((e) => e.isConfirmedEntity)
      .map((e) => ({ value: e.uuid, text: e.name })),
    editAssetsSimplified: variantSimplifiedAssets(state),
    organizationName: organization.name,
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'assets', uuid, organization.organizationId);
      return command.draft as AssetViewEditItem;
    },
  };
}
